/**
 * @file dcp/src/dcp-client/worker/supervisor2/sandbox-backoff-manager.js
 *
 * SandboxFactory implements sandbox creation in the presence of rate-limiting and load-limiting.
 * Any sandbox creation must go through SandboxFactory.
 *
 * @author Paul, paul@distributive.network
 * @date Aug 2025,
 * @module SandboxFactory
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */
'use strict';

const { assert } = require('dcp/common/dcp-assert');
const { selectiveDebug, selectiveSupEx } = require('./common');

/** @typedef {import('./sandbox2').Sandbox} Sandbox    */
/** @typedef {import('./index').Supervisor} Supervisor */

/**
 * Let level=#-of-non-terminated sandboxes, then if we cannot create a sandbox at a given level we
 * start a backoff mechanism only at that level so we don't keep failing to create sandboxes at a
 * really fast rate. When sandbox creation succeeds then that level is marked as having no backoff.
 * So a sandbox failure at level 4 causes backoff at level 4 but not at level 3 or below.
 * This is to handle the scenario where sandbox-creation is being loaded-limited at a level < maxSandboxes.
 */
class SandboxBackoffManager
{
  /** @param {Supervisor} supervisor */
  constructor (supervisor)
  {
    // Initialize the (effectively) non-mutable properties.
    /** @type {Supervisor} */
    this.supervisor   = supervisor;
    // 1 or more successive failures at a given size delays the next call to sandbox.create
    this.threshold    = this.supervisor.options.consecutiveSandboxFailureLimit;
    this.initialDelay = 1;  // seconds
    this.maxDelay     = this.supervisor.options.sandboxCreationMaxRetry; // seconds
    this.maxDiff      = Math.log2(Math.ceil(this.maxDelay / this.initialDelay));

    // Initialize the mutable properties.
    this.reset();
  }
  /**
   * Reset SandboxBackoffManager.
   * E.g. After the evaluator goes down, call reset() when the evaluator goes up.
   * It's a good idea to call reset() when there have been a large # of interferring terminate calls.
   */
  reset ()
  {
    // These properties are mutable.
    this.failures     = [];
    this.initialTime  = [];
    this.maxSandboxes = 0;
    this.createCriticalRegion    = false;
    this.terminateCriticalRegion = false;
    this.pendingTerminate        = [];
  }
  /**
   * Allow growth of failures and initialTime when maxSandboxes increases.
   * When maxSandboxes decreases we don't change failures and initialTime.
   * Return #non-terminated-sandboxes.
   * @returns {number} - #non-terminated-sandboxes.
   */
  #update ()
  {
    // Check for growth of maxSandboxes.
    for (let k = this.maxSandboxes; k <= this.supervisor.options.maxSandboxes; k++)
      this.failures[k] = this.initialTime[k] = 0;
    this.maxSandboxes = this.supervisor.options.maxSandboxes;
    // Count the non-terminated-sandboxes.
    const idx = this.#size();
    return idx;
  }
  /**
   * incrementIndex() enables and possibly increases a backoff delay at level idx = #size().
   *
   * Let idx = #size() be #non-terminated-sandboxes.
   * incrementIndex() is called in the catch handler of sandbox.start.
   * It will increase failures[idx] by 1 and set initialTime[idx] to current time, where
   * idx = #size() is the # of non-terminated-sandboxes.
   *
   * If there has been a sandbox.terminate call while waiting for sandbox.start to throw, the #size()
   * may be smaller than it was at the start of sandbox.start.  But we still use idx = #size() for the
   * failures and initialTime arrays. This is because the terminate call may have happened before or
   * after sandbox creation failure so we don't really know whether we failed to created a sandbox at
   * the level of idx=#size() when calling incrementIndex or idx+1. So we pick the conservative one
   * at idx.  Such a race will be fairly rare, so the true level will eventually be reflected in the
   * state of failures and initialTime. However, when a worker has a large # of sandboxes, the race
   * could happen more often. We mitigate this possibility by caching all sandboxes that want to call
   * sandbox.terminate(false) and then terminating them in the finally block of sandbox.start.
   *
   * A sandbox.terminate(true or error) call will not be delayed, so it can cause the level, idx, to
   * be inaccurate.  This is almost always due to sandboxHandle 'end' event handling, which
   * currently happens only when the evaluator goes down or onerror or sup.dropSandboxes are called
   * or a rare failure in onmessage.
   *
   * What happens when the level, idx, is inaccurate?
   * Well, the backoff mechanism in sandboxBackoffManager will be inaccurate for a fairly short period,
   * since sandbox.terminate(true or error) calls are rare outside of sandboxHandle 'end' event handling.
   */
  incrementIndex ()
  {
    const idx = this.#update();
    this.failures[idx]++;
    this.initialTime[idx] = Date.now();
  }
  /**
   * resetIndex() clears any backoff delay at level idx = #size().
   *
   * Let idx  = #size() be #non-terminated-sandboxes.
   * resetIndex() is called at the end of the try block of sandbox.start.
   * It will set failures[idx] = 0 and initialTime[idx] = 0, where
   * idx = #size() is the # of non-terminated-sandboxes.
   *
   * If there has been a sandbox.terminate call while waiting for sandbox.start to successfully complete,
   * the #size() may be smaller than it was at the start of sandbox.start.  But we still use idx = #size()
   * for the failures and initialTime arrays. This is because the terminate call may have happened
   * before or after sandbox creation success so we don't really know whether we successfully created
   * a sandbox at the level of idx=#size() when calling resetIndex or idx+1. So we pick the conservative
   * one at idx.  Such a race will be fairly rare, so the true level will eventually be reflected in the
   * state of failures and initialTime. However, when a worker has a large # of sandboxes, the race
   * could happen more often. We mitigate this possibility by caching all sandboxes that want to call
   * sandbox.terminate(false) and then terminating them in the finally block of sandbox.start.
   *
   * A sandbox.terminate(true or error) call will not be delayed, so it can cause the level, idx, to
   * be inaccurate.  This is almost always due to sandboxHandle 'end' event handling, which
   * currently happens only when the evaluator goes down or onerror or sup.dropSandboxes are called
   * or a rare failure in onmessage.
   *
   * What happens when the level, idx, is inaccurate? The reset call is at a level 1 below the true level,
   * in other words we record the successfull sandbox creation to be at a level 1 below the true level.
   * Well, the backoff mechanism in sandboxBackoffManager will be inaccurate for a fairly short period,
   * since sandbox.terminate(true or error) calls are rare outside of sandboxHandle 'end' event handling.
   */
  resetIndex ()
  {
    const idx = this.#update();
    this.failures[idx] = 0;
    this.initialTime[idx] = 0;
  }
  /**
   * Return the # of non-terminated sandboxes in inventory.
   * @returns {number}
   */
  #size ()
  {
    let idx = 0;
    for (const sbx of this.supervisor.sandboxInventory)
      if (!sbx.isTerminated)
        idx++;
    // It's possible there can be more than maxSandboxes, but it should be rare.
    if (this.failures[idx] === undefined)
      this.failures[idx] = this.initialTime[idx] = 0;
    assert(this.initialTime[idx] !== undefined);
    return idx;
  }
  /**
   * Compute the backoff delay, if any, at level idx.
   * @param {number} idx
   * @returns {number} - the delay in milliseconds
   */
  #delayUntilMs (idx)
  {
    const diff = this.failures[idx] - this.threshold;
    if (diff < 0)
      return 0;
    let delay = this.initialDelay;
    if (diff > 0)
    {
      const maxDiff = Math.min(diff, this.maxDiff);
      for (let k = 0; k < maxDiff; k++)
        delay *= 2;
    }
    selectiveDebug() && console.debug('SandboxBackoffManager.delayUntilMs:', delay);
    return delay * 1000 + this.initialTime[idx];
  }

  /**
   * Wrapper around createSandboxWithTimeout where there's an early exit until #delayUntilMs() expires.
   * In other words, before #delayUntilMs() expires, create() will fail to create a sandbox.
   * @returns {Promise<Sandbox>}
   */
  async create ()
  {
    const idx = this.#update();
    const delay = this.#delayUntilMs(idx);
    if (delay > Date.now())
    {
      selectiveSupEx() && console.debug(`SandboxBackoffManager.create: Need to wait ${(delay-Date.now())*1e-3} more seconds`, this.failures, this.supervisor.sandboxInventory.filter((sbx) => !sbx.isTerminated).map((sbx) => sbx.identifier));
      return null;
    }

    // Ask for room for 1 sandbox.
    this.supervisor.pruneSandboxes(1);
    selectiveDebug() && console.debug('SandboxBackoffManager.create: Calling createSandboxWithTimeout', this.supervisor.sandboxInventory.length);
    return this.supervisor.createSandboxWithTimeout(/*throwError*/ false);
  }
}
exports.SandboxBackoffManager = SandboxBackoffManager;
